import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@apollo/client";

import Loading from "ui/Loading";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import PageContent from "ui/PageContent";
import SettingsNav from "ui/SettingsNav";
import { useRoutes } from "utils/routes";

import {
  ServerSettingsList as ServerSettingsListType,
  ServerSettingsListVariables,
  ServerSettingsList_getServers as ServerType,
} from "./types/ServerSettingsList";
import QUERY from "./Query.graphql";
import styles from "./style.module.scss";
import { StatusDotIcon } from "ui/Icons";
import Grid from "ui/Grid";
import { makeFilter } from "utils/filter";
import { useQueryStringSearch } from "ui/QueryStringSearch";

type RowDataType = ServerType & {
  collectorVersion: string;
};

const ServerSettingsList: React.FunctionComponent = () => {
  const { slug: organizationSlug } = useParams();
  const [searchTerm, SearchInput] = useQueryStringSearch("q");
  const [showInactive, setShowInactive] = useState(false);
  const { data, loading, error } = useQuery<
    ServerSettingsListType,
    ServerSettingsListVariables
  >(QUERY, { variables: { organizationSlug } });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const titleSearch = (
    <>
      <div className={styles.showInactiveControl}>
        <input
          type="checkbox"
          checked={showInactive}
          id="show_inactive"
          onChange={(evt) => setShowInactive(evt.target.checked)}
        />
        <label htmlFor="show_inactive">
          Show servers with no recent statistics data
        </label>
      </div>
      <SearchInput />
    </>
  );

  const servers = data.getServers.map((s: ServerType): RowDataType => {
    return {
      ...s,
      collectorVersion: s.lastSnapshot?.collectorVersionStr,
    };
  });

  const gridData = servers
    .filter(makeFilter(searchTerm, "name"))
    .filter((s) => showInactive || s.hasRecentSnapshot);

  return (
    <PageContent
      title="Settings"
      pageCategory="servers"
      pageName="edit"
      featureNav={<SettingsNav />}
    >
      <Panel title="All Servers" secondaryTitle={titleSearch}>
        <PanelSection>Select a server to view settings:</PanelSection>
        <Grid
          striped
          defaultSortBy="name"
          data={gridData}
          columns={[
            {
              field: "name",
              header: "Server",
              width: "minmax(30%,1fr)",
              renderer: ServerNameCell,
            },
            {
              field: "displaySystemType",
              header: "System type",
              width: "20%",
            },
            {
              field: "collectorVersion",
              header: "Collector Version",
              width: "20%",
            },
            {
              field: "hasRecentSnapshot",
              header: "Has Recent Snapshot?",
              width: "20%",
              renderer: function HasRecentSnapshotCell({ fieldData }) {
                return fieldData ? (
                  <>
                    <StatusDotIcon
                      status="okay"
                      title="Recently received statistics data"
                    />
                    Yes
                  </>
                ) : (
                  <>
                    <StatusDotIcon status="no data" />
                    No
                  </>
                );
              },
            },
          ]}
        />
      </Panel>
    </PageContent>
  );
};

const ServerNameCell: React.FunctionComponent<{ rowData: RowDataType }> = ({
  rowData,
}) => {
  const { serverEdit } = useRoutes();
  return (
    <span>
      <Link to={serverEdit(rowData.humanId)}>{rowData.name}</Link>
    </span>
  );
};

export default ServerSettingsList;
